import { useState } from "react";
import { NavLink, Outlet, Link, Navigate } from "react-router-dom";
import { LayoutDashboard, Package, PlusCircle, FolderTree, Boxes, ShoppingCart, Settings, Users, ScrollText, Scale, Rocket, Menu, ExternalLink, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/admin/prodotti", label: "Prodotti", icon: Package, id: "products", end: true },
  { to: "/admin/prodotti/nuovo", label: "Nuovo prodotto", icon: PlusCircle, id: "new-product" },
  { to: "/admin/categorie", label: "Categorie", icon: FolderTree, id: "categories" },
  { to: "/admin/magazzino", label: "Magazzino", icon: Boxes, id: "inventory" },
  { to: "/admin/ordini", label: "Ordini", icon: ShoppingCart, id: "orders" },
  { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings, id: "settings", owner: true },
  { to: "/admin/pagine-legali", label: "Pagine legali", icon: Scale, id: "legal", owner: true },
  { to: "/admin/utenti", label: "Utenti", icon: Users, id: "users", owner: true },
  { to: "/admin/registro", label: "Registro attività", icon: ScrollText, id: "audit", owner: true },
  { to: "/admin/pre-lancio", label: "Verifica pre-lancio", icon: Rocket, id: "prelaunch", owner: true },
];

const Nav = ({ onNavigate, isOwner }) => (
  <div className="flex flex-col h-full">
    <div className="px-6 py-6 border-b border-[#F5F0E8]/10">
      <Logo light size="admin" to="/admin" testId="admin-logo" />
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#D5B98C] mt-3">Pannello amministrativo</div>
    </div>
    <nav className="flex-1 py-4 overflow-y-auto">
      {LINKS.filter((l) => !l.owner || isOwner).map(({ to, label, icon: Icon, id, end }) => (
        <NavLink key={id} to={to} end={end} onClick={onNavigate} data-testid={`admin-sidebar-${id}-link`}
          className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-sm transition-colors ${isActive ? "bg-[#F5F0E8]/10 text-[#F5F0E8] border-l-2 border-[#D5B98C]" : "text-[#F5F0E8]/60 hover:text-[#F5F0E8] border-l-2 border-transparent"}`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
        </NavLink>
      ))}
    </nav>
    <Link to="/" data-testid="admin-back-to-shop" className="flex items-center gap-2 px-6 py-5 text-xs text-[#F5F0E8]/60 hover:text-[#D5B98C] border-t border-[#F5F0E8]/10">
      <ExternalLink className="w-3.5 h-3.5" /> Vai alla boutique
    </Link>
  </div>
);

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout, isOwner } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#F5F0E8]" data-testid="admin-loading" />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return (
    <div className="min-h-screen flex bg-[#F5F0E8]" data-testid="admin-layout">
      <aside className="hidden lg:block w-64 shrink-0 bg-[#242321] sticky top-0 h-screen"><Nav isOwner={isOwner} /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="bg-[#242321] p-0 w-64 border-none">
          <SheetTitle className="sr-only">Menu admin</SheetTitle>
          <Nav isOwner={isOwner} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0">
        <header className="h-16 border-b border-[#D5B98C]/50 flex items-center justify-between gap-3 px-4 sm:px-8 bg-[#F5F0E8]/90 backdrop-blur sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button data-testid="admin-mobile-menu-button" className="lg:hidden" onClick={() => setOpen(true)}><Menu className="w-5 h-5" /></button>
            <span className="font-serif text-lg text-[#39251D] lg:hidden">BDC-Boutique</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="hidden sm:block text-right leading-tight" data-testid="admin-current-user">
              <span className="block font-medium">{user.name}</span>
              <span className="text-[#956842] uppercase tracking-widest text-[10px]">{user.role}</span>
            </span>
            <button data-testid="admin-logout-button" onClick={logout} className="p-2 border border-[#D5B98C] hover:bg-[#39251D] hover:text-[#F5F0E8] transition-colors" aria-label="Esci"><LogOut className="w-4 h-4" /></button>
          </div>
        </header>
        <main className="p-4 sm:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
